import {
    AreaField,
    CurrencyType,
    DatePickerField,
    InputField,
    ProviderCreateCollectionPaymentPayload,
    ToastContext,
} from '@enter/core';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { DialogContext } from 'services';
import { RootStore } from 'stores';
import * as Yup from 'yup';
import * as S from './invoice-item-dialog.styles';

type InvoiceItemDialogProps = {
    claimId: string;
    onClose: () => void;
};

const schema = Yup.object().shape({
    paymentDate: Yup.string().required('Payment date is required'),
    collectedAmount: Yup.object().shape({
        amount: Yup.string().required('Collected amount is required'),
    }),
});

type PaymentPayload = ProviderCreateCollectionPaymentPayload;

const initial: PaymentPayload = {
    collectedAmount: {
        amount: '0',
        currency: CurrencyType.USD,
    },
    claimId: '',
    paymentDate: '',
    memo: '',
};

export const CollectionPaymentDialog: React.FunctionComponent<InvoiceItemDialogProps> = inject('rootStore')(
    observer(
        (props: InvoiceItemDialogProps): React.ReactElement => {
            const dialogContext = useContext(DialogContext);
            const toastContext = useContext(ToastContext);

            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }

            const { claimStore } = rootStore;
            const { claimId, onClose } = props;

            const handleCancel = (): void => {
                onClose?.();
            };

            const handleSubmit = async (
                payment: PaymentPayload,
                { setSubmitting }: FormikActions<any>,
            ): Promise<void> => {
                setSubmitting(true);

                // Reverse payment amount for refunds
                // if (payment.paymentType === 'REFUND') {
                //     payment.collectedAmount = {
                //         amount: payment.collectedAmount.amount
                //             ? (parseFloat(payment.collectedAmount.amount) * -1).toString()
                //             : '0',
                //         currency: payment.collectedAmount.currency,
                //     };
                // }

                try {
                    await claimStore.createCollectionPayment({ ...payment, claimId });
                    toastContext.notify?.('Collection payment was recorded for this claim.', 'right');
                } catch (err) {
                    dialogContext.showError(err);
                } finally {
                    setSubmitting(false);
                    onClose?.();
                }
            };

            return (
                <S.DialogBody>
                    <Formik initialValues={initial} validationSchema={schema} onSubmit={handleSubmit}>
                        {(formik: FormikProps<PaymentPayload>): React.ReactElement => (
                            <>
                                <S.InputRow>
                                    <S.InputName>
                                        <S.Label>Payment Date</S.Label>
                                        <S.FormField
                                            name="paymentDate"
                                            component={DatePickerField}
                                            customDayPickerProps={{
                                                disabledDays: [
                                                    {
                                                        after: new Date(),
                                                    },
                                                ],
                                            }}
                                        />
                                    </S.InputName>
                                    <S.InputAmount>
                                        <S.Label>Amount</S.Label>
                                        <S.FormField
                                            name="collectedAmount.amount"
                                            maxLength={100}
                                            component={InputField}
                                            type="number"
                                        />
                                    </S.InputAmount>
                                </S.InputRow>
                                <S.InputMemo>
                                    <S.Label>Notes</S.Label>
                                    <S.FormField name="memo" component={AreaField} />
                                </S.InputMemo>
                                <S.ButtonGroup
                                    changed={true}
                                    useNotify={false}
                                    onCancel={handleCancel}
                                    onSave={formik.submitForm}
                                    saveLabel="Save"
                                    saveNotice="Item added"
                                />
                            </>
                        )}
                    </Formik>
                </S.DialogBody>
            );
        },
    ),
);
